/**
 * Placing ONE planner order on an investor's behalf, in a popup, without
 * leaving the plan.
 *
 * NOTHING HERE IS A NEW CONTRACT. Every network call is the admin panel's own
 * `mfAdminInvestments` wrapper, unchanged — the same authoritative per-ISIN
 * validation, the same create → confirm → pay sequence for a lumpsum, the same
 * mandate → bank authorization → verify → plan → confirm → sync sequence for a
 * SIP, and the same `/mf-payment-return` postback the investor dashboard uses.
 * The only thing this module adds is the popup, because the planner cannot
 * navigate the tab away from an in-memory plan.
 *
 * NO CONSENT OTP. The user platform gates each placement behind an OTP the
 * investor types; an admin acts directly on the investor's behalf, so that
 * layer is absent here. The provider's payment page and the bank's e-mandate
 * authorization are NOT part of that layer and are preserved in full.
 *
 * DUPLICATE SAFETY. A placement that gets as far as creating something at the
 * gateway records it on the caller's `ticket`. A retry reads the ticket and
 * refuses to create a second purchase or a second mandate. The only steps it
 * will re-run are the two that provably create nothing: reconciling a payment
 * against an existing purchase, and re-reading a mandate the bank may still be
 * approving.
 */

import {
  completeAdminLumpsumPayment,
  completeAdminSip,
  getMFErrorMessage,
  isMFMandatePendingError,
  placeAdminLumpsum,
  startAdminSip,
} from "./mfAdminInvestments";
import type { MFScheme, MFSchemeDetails } from "./mfAdminInvestments";
import {
  awaitPopupReturn,
  popupReturnUrl,
} from "../Helpers/mfPopupReturn";

export type PlannerPlacementStage =
  | "creating"
  | "authorising"
  | "waiting"
  | "confirming"
  | "syncing"
  | "done"
  | "failed";

export interface PlannerPlacementProgress {
  stage: PlannerPlacementStage;
  message: string;
}

/**
 * What already exists at the gateway for this order. Mutated in place as each
 * step succeeds so the caller keeps it even when the placement then throws.
 */
export interface PlannerPlacementTicket {
  purchaseId?: string;
  mandateId?: string;
  purchasePlanId?: string;
  /** True when the failure is one a retry may safely re-run. */
  resumable?: boolean;
}

export type PlannerProgressReporter = (
  progress: PlannerPlacementProgress
) => void;

export interface PlannerOrderTarget {
  userUuid: string;
  /** Route the popup is told to return to, e.g. "/mf_portfolio_planner/<uuid>". */
  returnPath: string;
  schemeDetails: MFSchemeDetails;
  amount: number;
}

export interface PlannerSipTarget extends PlannerOrderTarget {
  /** API form — lowercase, e.g. "monthly". */
  frequency: string;
  /** Omitted on a daily SIP. */
  installmentDay?: number;
  installments: number;
}

const schemeOf = (details: MFSchemeDetails): MFScheme => details.data;

/**
 * One-time investment: create → confirm → open payment → reconcile.
 *
 * `placeAdminLumpsum` re-reads the authoritative scheme details and re-validates
 * the amount against the published band, step, product multiple and operation
 * flag before it creates anything, so nothing is trusted from the plan.
 */
export const placePlannerLumpsum = async (
  target: PlannerOrderTarget,
  popup: Window,
  report: PlannerProgressReporter,
  ticket: PlannerPlacementTicket,
  signal?: { cancelled: boolean }
): Promise<{ purchaseId: string }> => {
  if (ticket.purchaseId) {
    throw new Error(
      `This line already created purchase ${ticket.purchaseId}. Reconcile or cancel it from the investor's Orders tab rather than placing it a second time.`
    );
  }

  report({ stage: "creating", message: "Creating the purchase…" });
  const created = await placeAdminLumpsum({
    userUuid: target.userUuid,
    scheme: schemeOf(target.schemeDetails),
    amount: target.amount,
    returnUrl: popupReturnUrl(target.returnPath),
  });
  ticket.purchaseId = created.purchaseId;
  // From here on the purchase exists. Creating it again would duplicate it, so
  // the only step left to re-run is the reconciliation below.
  ticket.resumable = true;
  if (!created.redirectUrl) {
    throw new Error(
      `Purchase ${created.purchaseId} was created and confirmed, but no payment link was returned. Reconcile or cancel it from the investor's Orders tab.`
    );
  }

  report({ stage: "authorising", message: "Opening the payment page…" });
  popup.location.href = created.redirectUrl;

  report({ stage: "waiting", message: "Waiting for the payment to complete…" });
  await awaitPopupReturn(popup, signal);

  return finishPlannerLumpsum(report, ticket);
};

/**
 * Reconciling a purchase whose payment window has been to the gateway.
 *
 * Safe to re-run: it PATCHes the existing purchase rather than creating one, so
 * it is also the recovery path when the popup was closed after the payment went
 * through but before the result got back here.
 */
export const finishPlannerLumpsum = async (
  report: PlannerProgressReporter,
  ticket: PlannerPlacementTicket
): Promise<{ purchaseId: string }> => {
  const purchaseId = ticket.purchaseId;
  if (!purchaseId) {
    throw new Error("There is no created purchase to reconcile for this line.");
  }
  report({ stage: "syncing", message: "Recording the payment…" });
  await completeAdminLumpsumPayment(purchaseId);
  ticket.resumable = false;
  report({ stage: "done", message: "Lumpsum order placed." });
  return { purchaseId };
};

/**
 * SIP: mandate → bank authorization → verify → plan → confirm → sync.
 *
 * The verification step is not ceremony. `completeAdminSip` checks the mandate
 * that came back is the one that was authorized, that the bank actually
 * approved it, and that its approved debit limit covers the instalment — a
 * mandate authorized for less would fail silently at the first debit, months
 * later.
 */
export const placePlannerSip = async (
  target: PlannerSipTarget,
  popup: Window,
  report: PlannerProgressReporter,
  ticket: PlannerPlacementTicket,
  signal?: { cancelled: boolean }
): Promise<{ purchasePlanId: string; mandateId: string; syncWarning: string }> => {
  if (ticket.purchasePlanId) {
    throw new Error(
      `This line already created SIP ${ticket.purchasePlanId}. Review it from the investor's Orders tab rather than placing it a second time.`
    );
  }
  if (ticket.mandateId) {
    throw new Error(
      `This line already created e-mandate ${ticket.mandateId}. Use "Resume after bank approval" rather than authorizing a second mandate.`
    );
  }

  report({ stage: "creating", message: "Creating the e-mandate…" });
  const started = await startAdminSip({
    userUuid: target.userUuid,
    scheme: schemeOf(target.schemeDetails),
    amount: target.amount,
    frequency: target.frequency,
    installmentDay: target.installmentDay,
    installments: target.installments,
    // Matches the user platform's planner: the first instalment follows the
    // schedule rather than debiting the moment the mandate is approved.
    firstInstallmentNow: false,
    returnUrl: popupReturnUrl(target.returnPath),
  });
  ticket.mandateId = started.mandateId;
  // The mandate exists now. Re-running the completion below can only ever read
  // it — it creates the purchase plan or throws before doing so — which makes
  // this line resumable until a completion attempt says otherwise.
  ticket.resumable = true;
  if (!started.redirectUrl) {
    throw new Error(
      `E-mandate ${started.mandateId} was created, but the bank authorization link was not returned. No SIP was created.`
    );
  }

  report({ stage: "authorising", message: "Opening bank authorization…" });
  popup.location.href = started.redirectUrl;

  report({ stage: "waiting", message: "Waiting for bank authorization…" });
  await awaitPopupReturn(popup, signal);

  return resumePlannerSip(target, report, ticket);
};

/**
 * The half of the SIP flow that runs after the bank has been to.
 *
 * Split out because it is the one step worth retrying on its own: when the bank
 * has received the mandate but not yet approved it, `completeAdminSip` throws
 * before creating anything at all, so re-running it cannot duplicate a SIP.
 */
export const resumePlannerSip = async (
  target: PlannerSipTarget,
  report: PlannerProgressReporter,
  ticket: PlannerPlacementTicket
): Promise<{ purchasePlanId: string; mandateId: string; syncWarning: string }> => {
  const mandateId = ticket.mandateId;
  if (!mandateId) {
    throw new Error("There is no authorized e-mandate to create this SIP against.");
  }
  if (ticket.purchasePlanId) {
    throw new Error(
      `This line already created SIP ${ticket.purchasePlanId}. Review it from the investor's Orders tab.`
    );
  }

  report({ stage: "confirming", message: "Verifying the mandate and creating the SIP…" });
  try {
    const completed = await completeAdminSip({
      userUuid: target.userUuid,
      mandateId,
      scheme: schemeOf(target.schemeDetails),
      amount: target.amount,
      frequency: target.frequency,
      installmentDay: target.installmentDay,
      installments: target.installments,
      firstInstallmentNow: false,
    });
    ticket.purchasePlanId = completed.purchasePlanId;
    ticket.resumable = false;
    report({
      stage: "done",
      message: completed.syncWarning ? "SIP confirmed." : "SIP placed.",
    });
    return {
      purchasePlanId: completed.purchasePlanId,
      mandateId,
      syncWarning: completed.syncWarning,
    };
  } catch (error) {
    // Only a still-pending mandate is safe to re-run: it throws before the
    // purchase plan is created. Everything else may have created one.
    ticket.resumable = isMFMandatePendingError(error);
    throw error;
  }
};

export const plannerPlacementError = (error: any): string =>
  getMFErrorMessage(error, "The order could not be placed for this investor.");
